import React, { useState } from 'react';
import { 
  Crown, 
  Sparkles, 
  ShieldCheck, 
  Layers, 
  Zap, 
  CheckCircle2, 
  Copy, 
  Check, 
  Download, 
  ArrowRight, 
  Cpu, 
  TrendingUp, 
  Target, 
  Globe, 
  Users, 
  Mic, 
  Terminal,
  ChevronDown,
  ChevronUp,
  Share2,
  FileText
} from 'lucide-react';

interface SovereignEngineMasterViewProps {
  showToast: (msg: string, type?: 'info' | 'success' | 'warning' | 'error') => void;
}

export const SOVEREIGN_YC_APPLICATION_DATA = {
  companyName: "Azura Sovereign™ (Azura Seraph Pty Ltd)",
  tagline: "The autonomous operating system for sovereign solo operators and accelerator cohorts.",
  oneLiner: "Azura Sovereign is the all-in-one autonomous enterprise stack unifying contract-to-cash margin defense (AzuraOS), non-sycophantic GTM diagnostics (Gemstar), and an AI Chief of Staff voice operations agent (Seraph) into one sovereign kernel.",
  
  whatIsYourCompanyGoingToMake: `Azura Sovereign is the complete autonomous operating kernel for the emerging class of "one-person enterprise" and high-agency cohort builders.

Historically, an independent founder or accelerator graduate must assemble an expensive, fragmented graveyard of 10+ disconnected SaaS tools—QuickBooks for accounting, HubSpot for CRM, Typefully for social distribution, Clay for lead scraping, and generic ChatGPT bots for strategy—spending $600+/month while remaining blind to their actual safe-to-spend cash and true market demand.

Azura Sovereign replaces this fragmented chaos with an integrated, three-layered engine of operational sovereignty:
1. The Financial Kernel (AzuraOS): An air-gapped contract-to-cash margin defense engine. 0.05-second client-side reconciliation, automated 50% milestone deposits, multi-currency FX netting, and automated statutory tax reserve calculations (IRS, HMRC, SARS, ATO, EU VAT) with zero bank credential scraping.
2. The Commercial Truth Engine (Gemstar GTM): A non-sycophantic diagnostic engine that stress-tests ICP resonance, value propositions, and customer willingness-to-pay with cold-blooded, adversarial objectivity—refusing to flatter founders into building products nobody buys.
3. The Operational Velocity Agent (Azura Seraph™): An executive AI Chief of Staff featuring an immersive real-time voice chamber, multi-format X/LinkedIn distribution studio, 7-stage enterprise pilot CRM, and daily 7:00 AM War Room memos with compute/runway modeling.

Together, these three components form a unified sovereign desktop where one builder operates with the strategic rigor and distribution firepower of a 15-person venture-backed team.`,

  whatIsNewAboutWhatYouMake: `Two fundamental breakthroughs:

1. Dogfooded Phased Synthesis (Not SaaS Feature Bloat):
Most "all-in-one" tools fail because they are shallow wrappers created in a vacuum. Azura Sovereign was engineered through three distinct operational survival phases under live fire:
- Phase I (Capital Defense): We built AzuraOS because we experienced cross-border clients losing up to 50% of top-line revenue to unhedged processor fees, FX slippage, and surprise tax assessments.
- Phase II (Market Truth): We built Gemstar because LLMs are pathologically agreeable and sycophantic. Founders need adversarial truth, not a cheerleader, to survive customer discovery.
- Phase III (Execution Velocity): We built Azura Seraph because a sovereign solo operator cannot waste 25 hours a week context-switching between outreach emails, social drafts, and runway calculations.

2. Air-Gapped Local-First Architecture with Sovereign Identity:
Unlike legacy ERPs that harvest founder banking credentials (Plaid/Yodlee liabilities) or store confidential business plans on vulnerable centralized servers, Azura Sovereign runs with an air-gapped dual-vault architecture. Financial statement parsing and margin calculation occur 100% in-browser in sub-50 milliseconds. Strategic AI queries use client-side sanitized metadata and BYOK (Bring Your Own Key) protocols, guaranteeing institutional-grade sovereignty.`,

  howDoYouMakeMoney: `We monetize through a high-margin, dual-engine distribution model:

1. Self-Serve Sovereign Founder Subscriptions:
- Core Sovereign Desk: $29/mo (or R499/mo localized in emerging markets) for solo operators, independent consultants, and boutique studio leads.
- Sovereign Pro / Seraph Voice Ops: $79/mo including unlimited voice chamber briefings, automated multi-format GTM distribution, and enterprise pilot CRM.

2. Institutional Cohort & Accelerator Licensing ($2,500 – $15,000 / Cohort):
- Accelerators, incubators (e.g., AltaLab, Techstars, regional hubs), and venture studios license Azura Sovereign for their active founder cohorts.
- Benefits to the accelerator: Program directors receive real-time, objective visibility into cohort financial runway, invoice collection speed, and diagnostic GTM health—dramatically reducing the 82% cash-flow failure rate of early-stage startups.

3. Merchant of Record Infrastructure:
Integrated payment processing via Dodo Payments enables seamless cross-border subscription checkout across 150+ countries with automatic VAT/sales tax settlement.`,

  userTractionAndDogfooding: `1. Live Production & Complete Tri-Build Dogfooding:
AzuraOS is live in production at https://azuraos.app. Azura Seraph Pty Ltd runs 100% of its internal treasury, enterprise outreach, and GTM strategy on this exact integrated kernel.
2. Institutional Backing & Ecosystem Validation:
- Google for Startups Cloud Program: Start Tier Recipient ($2,000 infrastructure grant).
- Google Cloud Partner Advantage Member: Official validated member status.
- AltaLab Sprint Cohort 2026: Selected for active founder sprint.
- Slush 2026 (Helsinki): Startup profile formally accepted alongside top 25 global VCs.
- WIT-h Network: Formally inducted into the Women in Tech global network.
3. Proven Performance Metrics:
- Sub-50ms statement parser validated across US, UK, EU, and South African banking formats.
- 90%+ reduction in reconciliation time compared to traditional spreadsheet accounting.
- 15 private tester operators and compliance SaaS founders actively stress-testing workflow outputs.`,

  whyAreYouTheFounder: `I am Raksha Singh, Founder and Principal Architect of Azura Seraph™ Pty Ltd.

My background bridges three hyper-demanding disciplines:
1. Military Systems Analysis & Precision: High-stakes protocol design, fault tolerance, and unyielding discipline where failure is not an option.
2. Luxury Hospitality & High-End Operations: Understanding white-glove executive UX, psychological friction points, and customer retention.
3. Full-Stack Software Engineering: Sole author and architect of the entire Azura ecosystem—from the WebAssembly/crypto-vault ledger to the voice synthesis engines and multi-currency tax calculators.

I don't just understand this ICP conceptually; I am the ICP. I built Azura Sovereign out of existential operational necessity to run a lean, global, institutional-grade company from anywhere in the world without hiring a bloated back-office team.`,

  otherInvestableIdeasAnswer: `Question: "If you have already applied to YC with a different idea and changed it, or if you have another idea that might be more investable, tell us about it."

Answer:
"In previous submissions, we articulated individual vectors of this mission: first, AzuraOS as the hyper-focused contract-to-cash margin defense engine; second, Gemstar as the non-sycophantic GTM diagnostic engine. 

What our live dogfooding revealed is that these are not separate companies—they are the three interlocking pillars of a far larger, unified category: The Sovereign Enterprise Operating System.

A solo operator or accelerator graduate cannot succeed with just a ledger (AzuraOS) if their value proposition is delusional; that's why they need Gemstar. And they cannot succeed with just a diagnostic (Gemstar) if they lack the execution bandwidth to run pilot outreach and social distribution; that's why they need Azura Seraph.

By unifying these three builds into one sovereign kernel, we transform a collection of vertical tools into an unbreakable operating system for the next generation of 1-person billion-dollar enterprises. Accelerators don't want to buy three SaaS licenses per founder; they want to hand every founder a turnkey sovereign command bridge on Day 1."`,

  describeIn50Chars: "Autonomous OS for 1-person companies & cohorts",

  howFarAlong: `1. Live Production & Complete Tri-Build Dogfooding:
AzuraOS is deployed and live in production at https://azuraos.app with end-to-end tri-pillar integration. Azura Seraph Pty Ltd runs 100% of its internal treasury, enterprise outreach, and GTM strategy on this exact integrated kernel.

2. Institutional Backing & Ecosystem Validation:
- Google for Startups Cloud Program: Start Tier Recipient ($2,000 infrastructure grant).
- Google Cloud Partner Advantage Member: Official validated member status.
- AltaLab Sprint Cohort 2026: Selected for active founder sprint.
- Slush 2026 (Helsinki): Startup profile formally accepted alongside top 25 global VCs.
- WIT-h Network: Formally inducted into the Women in Tech global network.

3. Live User Validation & Technical Performance:
- 15 private tester operators and compliance SaaS founders actively stress-testing ledger reconciliation, pilot pipeline SLA generation, and GTM diagnostic matrices.
- Sub-50ms statement parser validated across US, UK, EU, and South African banking formats.
- Dodo Payments Merchant of Record operational for cross-border subscription checkout across 150+ countries with automated tax settlement.
- Air-gapped local-first architecture operates with 0% reliance on third-party bank credential scrapers (anti-Plaid liability).`,

  techStack: `1. Frontend & Client-Side Vault:
- React 19, TypeScript, Vite, Tailwind CSS, Lucide icons, Canvas API visualizers.
- Local-first architecture: Web Cryptography API (AES-GCM client-side encryption) and in-memory WebAssembly/IndexedDB state. All PDF/CSV bank statement ingestion, FX conversion, and tax calculations run 100% in-browser in sub-50ms with zero bank credential scraping (anti-Plaid liability).

2. Backend & Cloud Infrastructure:
- Node.js runtime with Express API proxy gateway, Firebase Firestore for encrypted user settings/sublicenses, Firebase Authentication, Google Cloud Run microservices.
- Merchant of Record: Dodo Payments SDK for global tax-compliant subscription billing across 150+ countries.

3. AI Models & Multimodal Pipelines:
- Google Gemini 2.5/3 Pro and Flash via the @google/genai TypeScript SDK (utilizing structured JSON output, native function calling, and Multimodal Live API for real-time low-latency voice synthesis in the Chief of Staff chamber).
- Flexible Bring-Your-Own-Key (BYOK) provider routing supporting Anthropic Claude 3.5 Sonnet and OpenAI GPT-4o with client-side prompt sanitation.

4. AI Coding Tools:
- Google AI Studio, Gemini CLI/SDK developer tools, Cursor, and automated CI/CD linting and TypeScript verification harnesses.`,

  competitorsAndUnfairAdvantage: `Competitors:
1. Legacy Small Business Accounting: QuickBooks Online, Xero, FreshBooks.
2. Modern Fintech / Corporate Cards: Mercury, Brex, Ramp.
3. Solopreneur Client Management SaaS: HoneyBook, Bonsai, Dubsado.
4. Generalist AI Workspaces: ChatGPT Team, Claude Projects, Microsoft Copilot.

What We Understand That They Don’t:

1. Post-Mortem Audits vs. Pre-Execution Margin Defense:
QuickBooks and Xero are designed for accountants looking in the rearview mirror 3 weeks after money was spent. They rely on Plaid credential scraping which founders increasingly reject due to security leaks. We calculate true safe-to-spend profit and ring-fence statutory tax reserves (IRS, HMRC, SARS) in 0.05s client-side before contracts are executed and cash leaves the door.

2. Incentive Misalignment in Fintech:
Neobanks like Mercury and Ramp monetize card interchange—they win when you spend more. Azura Sovereign's entire architecture is engineered to protect margins, enforce 50% milestone deposits on incoming bookings, and prevent overspending.

3. The "Anti-Sycophancy" Mandate:
HoneyBook and Bonsai are passive invoice templates with zero financial intelligence. Generic LLMs (ChatGPT) are pathologically agreeable; they flatter founders and validate delusional business models. Our Gemstar diagnostic is intentionally non-sycophantic—it interrogates willingness-to-pay, flags pricing fallacies, and prevents founders from wasting sales runway on phantom demand.

4. The Accelerator B2B Cohort Distribution Wedge:
Competitors waste millions on direct-to-consumer Facebook/Google ads. We sell institutional cohort health licenses directly to accelerators, incubators, and studios (AltaLab, Techstars, regional hubs). Program directors gain real-time visibility into founder runway and invoice velocity, solving the #1 killer of cohort companies (cash-flow exhaustion).`,

  whatConvincedYouToApply: `What Convinced Us:
We are applying because of Y Combinator's explicit strategic thesis on the "One-Person Unicorn." Garry Tan, Dalton Caldwell, and YC leadership have highlighted that the next wave of iconic enterprises will be run by ultra-lean teams of 1 to 5 people wielding autonomous systems. 

While the broader ecosystem is still theorizing about this shift or releasing brittle single-prompt AI wrappers, we spent the last year living it: engineering an air-gapped, zero-dilution operational System of Record under live fire to run our own company. 

Encouragement & Proof:
Mentors and fellow founders in the AltaLab sprint and Google Cloud Partner Advantage network urged us to apply after seeing that Azura Sovereign is not a slide deck, but a fully functional, dogfooded platform solving the existential cash-flow and GTM failure points that kill 82% of early-stage startups.

Why Now:
We are not looking for YC to tell us what to build; the kernel is built and operating. We are applying because YC provides the world's highest-density pressure cooker to battle-test our assumptions, expand our institutional accelerator cohort licensing network, and establish Azura Sovereign as the definitive System of Record for the autonomous enterprise era.`
};

export function SovereignEngineMasterView({ showToast }: SovereignEngineMasterViewProps) {
  const [activeTab, setActiveTab] = useState<'blueprint' | 'yc_app' | 'strategy'>('blueprint');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [expandedSection, setExpandedSection] = useState<string | null>('q1');

  const copyToClipboard = (text: string, label: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    showToast(`📋 Copied ${label} to clipboard!`, 'success');
    setTimeout(() => setCopiedKey(null), 2500);
  };

  const downloadFullDossier = () => {
    const fullText = `# AZURA SOVEREIGN™ — THE UNIFIED SOVEREIGN ENTERPRISE ENGINE
## Y Combinator Master Application & Strategic Product Blueprint
Company: ${SOVEREIGN_YC_APPLICATION_DATA.companyName}
Tagline: ${SOVEREIGN_YC_APPLICATION_DATA.tagline}
50 Characters or Less: ${SOVEREIGN_YC_APPLICATION_DATA.describeIn50Chars}
One-Liner: ${SOVEREIGN_YC_APPLICATION_DATA.oneLiner}

=======================================================
DESCRIBE WHAT YOUR COMPANY DOES IN 50 CHARACTERS OR LESS
=======================================================
${SOVEREIGN_YC_APPLICATION_DATA.describeIn50Chars}

=======================================================
1. WHAT IS YOUR COMPANY GOING TO MAKE?
=======================================================
${SOVEREIGN_YC_APPLICATION_DATA.whatIsYourCompanyGoingToMake}

=======================================================
2. WHAT IS NEW ABOUT WHAT YOU MAKE?
=======================================================
${SOVEREIGN_YC_APPLICATION_DATA.whatIsNewAboutWhatYouMake}

=======================================================
3. HOW DO YOU MAKE MONEY?
=======================================================
${SOVEREIGN_YC_APPLICATION_DATA.howDoYouMakeMoney}

=======================================================
4. USER TRACTION & LIVE VALIDATION
=======================================================
${SOVEREIGN_YC_APPLICATION_DATA.userTractionAndDogfooding}

=======================================================
5. WHY ARE YOU THE FOUNDER TO BUILD THIS?
=======================================================
${SOVEREIGN_YC_APPLICATION_DATA.whyAreYouTheFounder}

=======================================================
6. HOW FAR ALONG ARE YOU?
=======================================================
${SOVEREIGN_YC_APPLICATION_DATA.howFarAlong}

=======================================================
7. WHAT TECH STACK ARE YOU USING? (AI MODELS & CODING TOOLS)
=======================================================
${SOVEREIGN_YC_APPLICATION_DATA.techStack}

=======================================================
8. COMPETITORS & WHAT DO YOU UNDERSTAND THAT THEY DON'T?
=======================================================
${SOVEREIGN_YC_APPLICATION_DATA.competitorsAndUnfairAdvantage}

=======================================================
9. WHAT CONVINCED YOU TO APPLY TO Y COMBINATOR?
=======================================================
${SOVEREIGN_YC_APPLICATION_DATA.whatConvincedYouToApply}

=======================================================
10. OTHER IDEAS / CONVERGENCE SYNTHESIS
=======================================================
${SOVEREIGN_YC_APPLICATION_DATA.otherInvestableIdeasAnswer}

=======================================================
THE THREE-PHASE ARCHITECTURAL PILLARS:
- Pillar 1: AzuraOS Kernel (Financial Truth & Contract-to-Cash Margin Defense)
- Pillar 2: Gemstar GTM Diagnostic (Commercial Truth & Non-Sycophantic ICP Validation)
- Pillar 3: Azura Seraph™ Ops Agent (Execution Truth, Real-Time Voice Chamber & Pilot CRM)
`;
    const blob = new Blob([fullText], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `AzuraSovereign_YC_Master_Synthesis_${new Date().toISOString().slice(0, 10)}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast('📥 Sovereign YC Master Dossier downloaded!', 'info');
  };

  return (
    <div className="space-y-8 text-left pb-28 sm:pb-20">
      {/* 1. HERO HEADER */}
      <div className="p-5 sm:p-7 bg-gradient-to-br from-[#121619] via-[#0E1214] to-[#0A0D0E] border border-amber-500/30 rounded-3xl shadow-2xl relative overflow-hidden space-y-6">
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl pointer-events-none" />
        
        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-5 border-b border-[#242C30] pb-6">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-400 flex items-center justify-center shrink-0 shadow-lg">
              <Crown size={28} />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-amber-400 bg-amber-500/10 px-2.5 py-0.5 rounded-full border border-amber-500/25">
                  THE SOVEREIGNTY TRINITY
                </span>
                <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-emerald-300 bg-emerald-500/10 px-2.5 py-0.5 rounded-full border border-emerald-500/25 flex items-center gap-1">
                  <CheckCircle2 size={11} /> 3-PHASE CONVERGENCE
                </span>
              </div>
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold font-display text-white tracking-tight">
                Azura Sovereign™ — Unified Engine of Sovereignty
              </h2>
              <p className="text-xs sm:text-sm text-gray-300 max-w-3xl leading-relaxed">
                The strategic synthesis unifying <strong className="text-teal-300">AzuraOS</strong> (Treasury &amp; Margin Defense), <strong className="text-amber-300">Gemstar</strong> (Non-Sycophantic GTM Truth), and <strong className="text-purple-300">Azura Seraph™</strong> (Chief of Staff Voice Ops) into one sovereign operating system for solo operators and cohort builders.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 w-full lg:w-auto shrink-0">
            <button
              onClick={downloadFullDossier}
              className="w-full sm:w-auto px-4 py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-extrabold text-xs rounded-xl shadow-lg transition flex items-center justify-center gap-2 cursor-pointer"
            >
              <Download size={14} />
              <span>Download Master Dossier (.md)</span>
            </button>
            <button
              onClick={() => copyToClipboard(SOVEREIGN_YC_APPLICATION_DATA.whatIsYourCompanyGoingToMake, "What is your company going to make", "hero_q1")}
              className="w-full sm:w-auto px-4 py-3 bg-[#171D20] hover:bg-[#20272B] text-gray-200 hover:text-white font-bold text-xs rounded-xl border border-[#2D363B] transition flex items-center justify-center gap-2 cursor-pointer"
            >
              {copiedKey === 'hero_q1' ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} className="text-amber-400" />}
              <span>Copy YC Core Pitch</span>
            </button>
          </div>
        </div>

        {/* 3 SUB-TABS: Responsive grid prevents cut-off on mobile */}
        <div className="pt-1">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
            <button
              onClick={() => setActiveTab('blueprint')}
              className={`w-full px-4 py-3 text-xs font-bold rounded-xl transition flex items-center justify-center sm:justify-start gap-2.5 cursor-pointer text-left ${
                activeTab === 'blueprint' 
                  ? 'bg-amber-400 text-black font-extrabold shadow-lg shadow-amber-500/20' 
                  : 'bg-[#15191C] text-gray-300 hover:text-white hover:bg-[#1C2226] border border-[#262E33]'
              }`}
            >
              <Layers size={15} className={activeTab === 'blueprint' ? 'text-black' : 'text-amber-400'} />
              <span>1. Tri-Engine Architecture</span>
            </button>

            <button
              onClick={() => setActiveTab('yc_app')}
              className={`w-full px-4 py-3 text-xs font-bold rounded-xl transition flex items-center justify-center sm:justify-start gap-2.5 cursor-pointer text-left ${
                activeTab === 'yc_app' 
                  ? 'bg-amber-400 text-black font-extrabold shadow-lg shadow-amber-500/20' 
                  : 'bg-[#15191C] text-gray-300 hover:text-white hover:bg-[#1C2226] border border-[#262E33]'
              }`}
            >
              <FileText size={15} className={activeTab === 'yc_app' ? 'text-black' : 'text-amber-400'} />
              <span>2. YC Master Application Answers</span>
            </button>

            <button
              onClick={() => setActiveTab('strategy')}
              className={`w-full px-4 py-3 text-xs font-bold rounded-xl transition flex items-center justify-center sm:justify-start gap-2.5 cursor-pointer text-left ${
                activeTab === 'strategy' 
                  ? 'bg-amber-400 text-black font-extrabold shadow-lg shadow-amber-500/20' 
                  : 'bg-[#15191C] text-gray-300 hover:text-white hover:bg-[#1C2226] border border-[#262E33]'
              }`}
            >
              <Sparkles size={15} className={activeTab === 'strategy' ? 'text-black' : 'text-amber-400'} />
              <span>3. Strategic Thesis &amp; Partner Review</span>
            </button>
          </div>
        </div>
      </div>

      {/* TAB 1: TRI-ENGINE ARCHITECTURE */}
      {activeTab === 'blueprint' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* PILLAR 1: AZURAOS KERNEL */}
            <div className="p-5 bg-[#0F1315] border border-teal-500/30 rounded-2xl flex flex-col justify-between space-y-4 hover:border-teal-500/60 transition shadow-lg group">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-xl bg-teal-500/10 border border-teal-500/30 text-teal-400 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <ShieldCheck size={20} />
                  </div>
                  <span className="text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-teal-500/10 text-teal-300 border border-teal-500/25">
                    PHASE I • TREASURY
                  </span>
                </div>
                <div>
                  <h3 className="text-base font-bold text-white group-hover:text-teal-200 transition">
                    AzuraOS Kernel
                  </h3>
                  <p className="text-xs text-teal-400 font-mono font-semibold">
                    The Financial Margin Defense Engine
                  </p>
                </div>
                <p className="text-xs text-gray-300 leading-relaxed">
                  Acts as the <strong>Autonomous CFO</strong>. Guarantees true net profit and safe-to-spend cash by defending margins before money leaves the bank.
                </p>
                <ul className="space-y-1.5 text-[11px] text-gray-400 font-mono">
                  <li className="flex items-center gap-1.5 text-gray-300">
                    <CheckCircle2 size={12} className="text-teal-400 shrink-0" />
                    <span>0.05s client-side air-gapped reconciliation</span>
                  </li>
                  <li className="flex items-center gap-1.5 text-gray-300">
                    <CheckCircle2 size={12} className="text-teal-400 shrink-0" />
                    <span>Zero bank password scraping (Anti-Plaid)</span>
                  </li>
                  <li className="flex items-center gap-1.5 text-gray-300">
                    <CheckCircle2 size={12} className="text-teal-400 shrink-0" />
                    <span>50% deposit enforcement on /book portal</span>
                  </li>
                  <li className="flex items-center gap-1.5 text-gray-300">
                    <CheckCircle2 size={12} className="text-teal-400 shrink-0" />
                    <span>Automated tax reserves (IRS, HMRC, SARS)</span>
                  </li>
                </ul>
              </div>
              <div className="pt-3 border-t border-[#1C2327] text-[11px] text-teal-300 font-mono font-bold">
                Principle: "Growth without margin defense is accelerated bankruptcy."
              </div>
            </div>

            {/* PILLAR 2: GEMSTAR GTM DIAGNOSTIC */}
            <div className="p-5 bg-[#0F1315] border border-amber-500/30 rounded-2xl flex flex-col justify-between space-y-4 hover:border-amber-500/60 transition shadow-lg group">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-400 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Target size={20} />
                  </div>
                  <span className="text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/25">
                    PHASE II • DIAGNOSTIC
                  </span>
                </div>
                <div>
                  <h3 className="text-base font-bold text-white group-hover:text-amber-200 transition">
                    Gemstar GTM Engine
                  </h3>
                  <p className="text-xs text-amber-400 font-mono font-semibold">
                    The Non-Sycophantic Commercial Truth
                  </p>
                </div>
                <p className="text-xs text-gray-300 leading-relaxed">
                  Acts as the <strong>Autonomous CMO &amp; Market Stress-Tester</strong>. Protects founders from echo chambers by ruthlessly interrogating ICP willingness-to-pay.
                </p>
                <ul className="space-y-1.5 text-[11px] text-gray-400 font-mono">
                  <li className="flex items-center gap-1.5 text-gray-300">
                    <CheckCircle2 size={12} className="text-amber-400 shrink-0" />
                    <span>Anti-flattery diagnostic evaluation</span>
                  </li>
                  <li className="flex items-center gap-1.5 text-gray-300">
                    <CheckCircle2 size={12} className="text-amber-400 shrink-0" />
                    <span>Value proposition stress testing</span>
                  </li>
                  <li className="flex items-center gap-1.5 text-gray-300">
                    <CheckCircle2 size={12} className="text-amber-400 shrink-0" />
                    <span>Willingness-to-pay validation matrices</span>
                  </li>
                  <li className="flex items-center gap-1.5 text-gray-300">
                    <CheckCircle2 size={12} className="text-amber-400 shrink-0" />
                    <span>Adversarial positioning &amp; moat auditing</span>
                  </li>
                </ul>
              </div>
              <div className="pt-3 border-t border-[#1C2327] text-[11px] text-amber-300 font-mono font-bold">
                Principle: "Generic AI flatters you; Gemstar tells you why you will fail."
              </div>
            </div>

            {/* PILLAR 3: AZURA SERAPH OPS AGENT */}
            <div className="p-5 bg-[#0F1315] border border-purple-500/30 rounded-2xl flex flex-col justify-between space-y-4 hover:border-purple-500/60 transition shadow-lg group">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/30 text-purple-400 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Mic size={20} />
                  </div>
                  <span className="text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-300 border border-purple-500/25">
                    PHASE III • VELOCITY
                  </span>
                </div>
                <div>
                  <h3 className="text-base font-bold text-white group-hover:text-purple-200 transition">
                    Azura Seraph™ Ops Agent
                  </h3>
                  <p className="text-xs text-purple-400 font-mono font-semibold">
                    The Chief of Staff &amp; Voice Chamber
                  </p>
                </div>
                <p className="text-xs text-gray-300 leading-relaxed">
                  Acts as the <strong>Autonomous COO</strong>. Drives external growth, enterprise outreach, high-speed social distribution, and daily operational discipline.
                </p>
                <ul className="space-y-1.5 text-[11px] text-gray-400 font-mono">
                  <li className="flex items-center gap-1.5 text-gray-300">
                    <CheckCircle2 size={12} className="text-purple-400 shrink-0" />
                    <span>Real-time voice chamber &amp; orbital visualizer</span>
                  </li>
                  <li className="flex items-center gap-1.5 text-gray-300">
                    <CheckCircle2 size={12} className="text-purple-400 shrink-0" />
                    <span>LinkedIn &amp; X viral thread &amp; carousel generator</span>
                  </li>
                  <li className="flex items-center gap-1.5 text-gray-300">
                    <CheckCircle2 size={12} className="text-purple-400 shrink-0" />
                    <span>7-stage enterprise pilot CRM &amp; SLA drafts</span>
                  </li>
                  <li className="flex items-center gap-1.5 text-gray-300">
                    <CheckCircle2 size={12} className="text-purple-400 shrink-0" />
                    <span>7:00 AM War Room memo &amp; runway calculator</span>
                  </li>
                </ul>
              </div>
              <div className="pt-3 border-t border-[#1C2327] text-[11px] text-purple-300 font-mono font-bold">
                Principle: "A sovereign founder out-executes 15-person teams via voice &amp; automation."
              </div>
            </div>
          </div>

          {/* THE CONVERGENCE ENGINE BOX */}
          <div className="p-6 bg-[#111618] border border-[#2A343A] rounded-2xl space-y-4">
            <div className="flex items-center gap-3">
              <Zap size={22} className="text-amber-400" />
              <h3 className="text-base font-bold text-white">
                The Unbroken Sovereign Flywheel: How the 3 Builds Work as One Single Desk
              </h3>
            </div>
            <p className="text-xs sm:text-sm text-gray-300 leading-relaxed">
              When an accelerator graduate or independent operator enters Azura Sovereign, they do not switch between disconnected apps:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-2 text-xs">
              <div className="p-3.5 bg-[#171D20] border border-[#273238] rounded-xl space-y-1.5">
                <span className="text-[10px] font-mono text-teal-400 font-bold uppercase">Step 1 • Ingest &amp; Defend</span>
                <div className="font-bold text-white">AzuraOS Kernel</div>
                <p className="text-gray-400 text-[11px] leading-relaxed">
                  Incoming client revenue is reconciled in 0.05s. Platform fees, FX markups, and statutory tax reserves are ring-fenced automatically.
                </p>
              </div>

              <div className="p-3.5 bg-[#171D20] border border-[#273238] rounded-xl space-y-1.5">
                <span className="text-[10px] font-mono text-amber-400 font-bold uppercase">Step 2 • Diagnose &amp; Calibrate</span>
                <div className="font-bold text-white">Gemstar Diagnostic</div>
                <p className="text-gray-400 text-[11px] leading-relaxed">
                  The founder stress-tests pilot pricing and ICP profiles. The engine flags sycophantic illusions before spending sales capital.
                </p>
              </div>

              <div className="p-3.5 bg-[#171D20] border border-[#273238] rounded-xl space-y-1.5">
                <span className="text-[10px] font-mono text-purple-400 font-bold uppercase">Step 3 • Distribute &amp; Close</span>
                <div className="font-bold text-white">Azura Seraph Agent</div>
                <p className="text-gray-400 text-[11px] leading-relaxed">
                  The AI Chief of Staff runs voice drills, generates viral LinkedIn/X threads, and deploys 3-step pilot SLA agreements to target leads.
                </p>
              </div>

              <div className="p-3.5 bg-[#171D20] border border-amber-500/40 rounded-xl space-y-1.5 bg-gradient-to-b from-[#1C2327] to-[#121618]">
                <span className="text-[10px] font-mono text-amber-300 font-bold uppercase">Step 4 • Sovereign Compounding</span>
                <div className="font-bold text-white">Zero Dilution / Zero Debt</div>
                <p className="text-gray-400 text-[11px] leading-relaxed">
                  Safe-to-spend cash feeds back into compute and runway reserves. The founder scales with institutional rigor without bloated overhead.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: YC MASTER APPLICATION ANSWERS */}
      {activeTab === 'yc_app' && (
        <div className="space-y-4">
          <div className="p-4 bg-[#101416] border border-[#263137] rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-gray-300">
            <div className="flex items-center gap-2">
              <Terminal size={16} className="text-amber-400 shrink-0" />
              <span>Calibrated strictly to Y Combinator application word limits and evaluation criteria.</span>
            </div>
            <button
              onClick={downloadFullDossier}
              className="text-amber-400 hover:text-amber-300 font-bold flex items-center gap-1.5 cursor-pointer shrink-0 self-start sm:self-auto"
            >
              <Download size={13} />
              <span>Export Complete Application</span>
            </button>
          </div>

          {/* 50-CHARACTER CRITICAL SPEC CARD */}
          <div className="p-4 bg-gradient-to-r from-amber-500/10 via-[#141A1D] to-[#101416] border border-amber-500/30 rounded-2xl flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-amber-400 bg-amber-500/15 px-2.5 py-0.5 rounded border border-amber-500/30">
                  YC Constraint: 50 Chars
                </span>
                <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/25">
                  Exact: 46 / 50 characters (Valid)
                </span>
              </div>
              <h4 className="text-xs font-bold text-gray-300">Describe what your company does in 50 characters or less.*</h4>
              <p className="text-sm font-mono font-bold text-amber-300 bg-black/40 px-3 py-1.5 rounded-lg border border-amber-500/20 inline-block">
                "{SOVEREIGN_YC_APPLICATION_DATA.describeIn50Chars}"
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => copyToClipboard(SOVEREIGN_YC_APPLICATION_DATA.describeIn50Chars, "50-character description", "50_char")}
                className="px-3.5 py-2.5 bg-amber-500 hover:bg-amber-400 text-black font-extrabold text-xs rounded-xl shadow transition flex items-center gap-1.5 cursor-pointer"
              >
                {copiedKey === '50_char' ? <Check size={14} className="text-black" /> : <Copy size={14} />}
                <span>Copy 50-Char Pitch</span>
              </button>
            </div>
          </div>

          {/* QUESTION 1 */}
          <div className="border border-[#263137] bg-[#111518] rounded-2xl overflow-hidden">
            <div className="w-full p-4 flex items-center justify-between text-left hover:bg-[#161C1F] transition">
              <button
                type="button"
                onClick={() => setExpandedSection(prev => prev === 'q1' ? null : 'q1')}
                className="flex-1 flex items-center gap-3 text-left cursor-pointer group"
              >
                <span className="w-6 h-6 rounded-lg bg-amber-400/10 text-amber-400 font-mono font-bold text-xs flex items-center justify-center shrink-0">
                  1
                </span>
                <div>
                  <h4 className="text-sm font-bold text-white group-hover:text-amber-300 transition-colors">What is your company going to make?</h4>
                  <span className="text-[10px] text-gray-400 font-mono">The Problem &amp; The Sovereign 3-Engine Solution</span>
                </div>
              </button>
              <div className="flex items-center gap-2 shrink-0 ml-3">
                <button
                  type="button"
                  onClick={() => copyToClipboard(SOVEREIGN_YC_APPLICATION_DATA.whatIsYourCompanyGoingToMake, "Question 1", "q1")}
                  className="p-1.5 text-gray-400 hover:text-amber-400 transition cursor-pointer rounded-lg hover:bg-[#20272B]"
                  title="Copy this answer"
                >
                  {copiedKey === 'q1' ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                </button>
                <button
                  type="button"
                  onClick={() => setExpandedSection(prev => prev === 'q1' ? null : 'q1')}
                  className="p-1.5 text-gray-400 hover:text-white transition cursor-pointer"
                  aria-label="Toggle answer accordion"
                >
                  {expandedSection === 'q1' ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                </button>
              </div>
            </div>

            {expandedSection === 'q1' && (
              <div className="p-5 border-t border-[#1F272B] bg-[#0E1214] text-xs text-gray-300 leading-relaxed whitespace-pre-wrap space-y-3 font-sans">
                {SOVEREIGN_YC_APPLICATION_DATA.whatIsYourCompanyGoingToMake}
              </div>
            )}
          </div>

          {/* QUESTION 2 */}
          <div className="border border-[#263137] bg-[#111518] rounded-2xl overflow-hidden">
            <div className="w-full p-4 flex items-center justify-between text-left hover:bg-[#161C1F] transition">
              <button
                type="button"
                onClick={() => setExpandedSection(prev => prev === 'q2' ? null : 'q2')}
                className="flex-1 flex items-center gap-3 text-left cursor-pointer group"
              >
                <span className="w-6 h-6 rounded-lg bg-amber-400/10 text-amber-400 font-mono font-bold text-xs flex items-center justify-center shrink-0">
                  2
                </span>
                <div>
                  <h4 className="text-sm font-bold text-white group-hover:text-amber-300 transition-colors">What is new about what you make?</h4>
                  <span className="text-[10px] text-gray-400 font-mono">Why existing solutions fail &amp; our architectural unfair advantage</span>
                </div>
              </button>
              <div className="flex items-center gap-2 shrink-0 ml-3">
                <button
                  type="button"
                  onClick={() => copyToClipboard(SOVEREIGN_YC_APPLICATION_DATA.whatIsNewAboutWhatYouMake, "Question 2", "q2")}
                  className="p-1.5 text-gray-400 hover:text-amber-400 transition cursor-pointer rounded-lg hover:bg-[#20272B]"
                  title="Copy this answer"
                >
                  {copiedKey === 'q2' ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                </button>
                <button
                  type="button"
                  onClick={() => setExpandedSection(prev => prev === 'q2' ? null : 'q2')}
                  className="p-1.5 text-gray-400 hover:text-white transition cursor-pointer"
                  aria-label="Toggle answer accordion"
                >
                  {expandedSection === 'q2' ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                </button>
              </div>
            </div>

            {expandedSection === 'q2' && (
              <div className="p-5 border-t border-[#1F272B] bg-[#0E1214] text-xs text-gray-300 leading-relaxed whitespace-pre-wrap space-y-3 font-sans">
                {SOVEREIGN_YC_APPLICATION_DATA.whatIsNewAboutWhatYouMake}
              </div>
            )}
          </div>

          {/* QUESTION 3 */}
          <div className="border border-[#263137] bg-[#111518] rounded-2xl overflow-hidden">
            <div className="w-full p-4 flex items-center justify-between text-left hover:bg-[#161C1F] transition">
              <button
                type="button"
                onClick={() => setExpandedSection(prev => prev === 'q3' ? null : 'q3')}
                className="flex-1 flex items-center gap-3 text-left cursor-pointer group"
              >
                <span className="w-6 h-6 rounded-lg bg-amber-400/10 text-amber-400 font-mono font-bold text-xs flex items-center justify-center shrink-0">
                  3
                </span>
                <div>
                  <h4 className="text-sm font-bold text-white group-hover:text-amber-300 transition-colors">How do you make money?</h4>
                  <span className="text-[10px] text-gray-400 font-mono">Business model, pricing tiers, and accelerator cohort licensing</span>
                </div>
              </button>
              <div className="flex items-center gap-2 shrink-0 ml-3">
                <button
                  type="button"
                  onClick={() => copyToClipboard(SOVEREIGN_YC_APPLICATION_DATA.howDoYouMakeMoney, "Question 3", "q3")}
                  className="p-1.5 text-gray-400 hover:text-amber-400 transition cursor-pointer rounded-lg hover:bg-[#20272B]"
                  title="Copy this answer"
                >
                  {copiedKey === 'q3' ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                </button>
                <button
                  type="button"
                  onClick={() => setExpandedSection(prev => prev === 'q3' ? null : 'q3')}
                  className="p-1.5 text-gray-400 hover:text-white transition cursor-pointer"
                  aria-label="Toggle answer accordion"
                >
                  {expandedSection === 'q3' ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                </button>
              </div>
            </div>

            {expandedSection === 'q3' && (
              <div className="p-5 border-t border-[#1F272B] bg-[#0E1214] text-xs text-gray-300 leading-relaxed whitespace-pre-wrap space-y-3 font-sans">
                {SOVEREIGN_YC_APPLICATION_DATA.howDoYouMakeMoney}
              </div>
            )}
          </div>

          {/* QUESTION 4 */}
          <div className="border border-[#263137] bg-[#111518] rounded-2xl overflow-hidden">
            <div className="w-full p-4 flex items-center justify-between text-left hover:bg-[#161C1F] transition">
              <button
                type="button"
                onClick={() => setExpandedSection(prev => prev === 'q4' ? null : 'q4')}
                className="flex-1 flex items-center gap-3 text-left cursor-pointer group"
              >
                <span className="w-6 h-6 rounded-lg bg-amber-400/10 text-amber-400 font-mono font-bold text-xs flex items-center justify-center shrink-0">
                  4
                </span>
                <div>
                  <h4 className="text-sm font-bold text-white group-hover:text-amber-300 transition-colors">What is your user traction and validation?</h4>
                  <span className="text-[10px] text-gray-400 font-mono">Google Cloud Partner, AltaLab, 15 testers, and dogfooding reality</span>
                </div>
              </button>
              <div className="flex items-center gap-2 shrink-0 ml-3">
                <button
                  type="button"
                  onClick={() => copyToClipboard(SOVEREIGN_YC_APPLICATION_DATA.userTractionAndDogfooding, "Question 4", "q4")}
                  className="p-1.5 text-gray-400 hover:text-amber-400 transition cursor-pointer rounded-lg hover:bg-[#20272B]"
                  title="Copy this answer"
                >
                  {copiedKey === 'q4' ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                </button>
                <button
                  type="button"
                  onClick={() => setExpandedSection(prev => prev === 'q4' ? null : 'q4')}
                  className="p-1.5 text-gray-400 hover:text-white transition cursor-pointer"
                  aria-label="Toggle answer accordion"
                >
                  {expandedSection === 'q4' ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                </button>
              </div>
            </div>

            {expandedSection === 'q4' && (
              <div className="p-5 border-t border-[#1F272B] bg-[#0E1214] text-xs text-gray-300 leading-relaxed whitespace-pre-wrap space-y-3 font-sans">
                {SOVEREIGN_YC_APPLICATION_DATA.userTractionAndDogfooding}
              </div>
            )}
          </div>

          {/* QUESTION 5 */}
          <div className="border border-[#263137] bg-[#111518] rounded-2xl overflow-hidden">
            <div className="w-full p-4 flex items-center justify-between text-left hover:bg-[#161C1F] transition">
              <button
                type="button"
                onClick={() => setExpandedSection(prev => prev === 'q5' ? null : 'q5')}
                className="flex-1 flex items-center gap-3 text-left cursor-pointer group"
              >
                <span className="w-6 h-6 rounded-lg bg-amber-400/10 text-amber-400 font-mono font-bold text-xs flex items-center justify-center shrink-0">
                  5
                </span>
                <div>
                  <h4 className="text-sm font-bold text-white group-hover:text-amber-300 transition-colors">Why are you the founder to build this?</h4>
                  <span className="text-[10px] text-gray-400 font-mono">Military systems analysis, luxury hospitality, and full-stack software velocity</span>
                </div>
              </button>
              <div className="flex items-center gap-2 shrink-0 ml-3">
                <button
                  type="button"
                  onClick={() => copyToClipboard(SOVEREIGN_YC_APPLICATION_DATA.whyAreYouTheFounder, "Question 5", "q5")}
                  className="p-1.5 text-gray-400 hover:text-amber-400 transition cursor-pointer rounded-lg hover:bg-[#20272B]"
                  title="Copy this answer"
                >
                  {copiedKey === 'q5' ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                </button>
                <button
                  type="button"
                  onClick={() => setExpandedSection(prev => prev === 'q5' ? null : 'q5')}
                  className="p-1.5 text-gray-400 hover:text-white transition cursor-pointer"
                  aria-label="Toggle answer accordion"
                >
                  {expandedSection === 'q5' ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                </button>
              </div>
            </div>

            {expandedSection === 'q5' && (
              <div className="p-5 border-t border-[#1F272B] bg-[#0E1214] text-xs text-gray-300 leading-relaxed whitespace-pre-wrap space-y-3 font-sans">
                {SOVEREIGN_YC_APPLICATION_DATA.whyAreYouTheFounder}
              </div>
            )}
          </div>

          {/* QUESTION 6: OTHER IDEAS / CONVERGENCE */}
          <div className="border border-[#263137] bg-[#111518] rounded-2xl overflow-hidden">
            <div className="w-full p-4 flex items-center justify-between text-left hover:bg-[#161C1F] transition">
              <button
                type="button"
                onClick={() => setExpandedSection(prev => prev === 'q6' ? null : 'q6')}
                className="flex-1 flex items-center gap-3 text-left cursor-pointer group"
              >
                <span className="w-6 h-6 rounded-lg bg-amber-400/10 text-amber-400 font-mono font-bold text-xs flex items-center justify-center shrink-0">
                  6
                </span>
                <div>
                  <h4 className="text-sm font-bold text-white group-hover:text-amber-300 transition-colors">
                    What other ideas do you have that might be more investable?
                  </h4>
                  <span className="text-[10px] text-amber-400 font-mono">
                    *The Secret Weapon Answer* — Framing the 3 phased builds as one cohesive category
                  </span>
                </div>
              </button>
              <div className="flex items-center gap-2 shrink-0 ml-3">
                <button
                  type="button"
                  onClick={() => copyToClipboard(SOVEREIGN_YC_APPLICATION_DATA.otherInvestableIdeasAnswer, "Question 6", "q6")}
                  className="p-1.5 text-gray-400 hover:text-amber-400 transition cursor-pointer rounded-lg hover:bg-[#20272B]"
                  title="Copy this answer"
                >
                  {copiedKey === 'q6' ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                </button>
                <button
                  type="button"
                  onClick={() => setExpandedSection(prev => prev === 'q6' ? null : 'q6')}
                  className="p-1.5 text-gray-400 hover:text-white transition cursor-pointer"
                  aria-label="Toggle answer accordion"
                >
                  {expandedSection === 'q6' ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                </button>
              </div>
            </div>

            {expandedSection === 'q6' && (
              <div className="p-5 border-t border-[#1F272B] bg-[#0E1214] text-xs text-gray-300 leading-relaxed whitespace-pre-wrap space-y-3 font-sans">
                {SOVEREIGN_YC_APPLICATION_DATA.otherInvestableIdeasAnswer}
              </div>
            )}
          </div>

          {/* QUESTION 7: HOW FAR ALONG ARE YOU? */}
          <div className="border border-[#263137] bg-[#111518] rounded-2xl overflow-hidden">
            <div className="w-full p-4 flex items-center justify-between text-left hover:bg-[#161C1F] transition">
              <button
                type="button"
                onClick={() => setExpandedSection(prev => prev === 'q7' ? null : 'q7')}
                className="flex-1 flex items-center gap-3 text-left cursor-pointer group"
              >
                <span className="w-6 h-6 rounded-lg bg-amber-400/10 text-amber-400 font-mono font-bold text-xs flex items-center justify-center shrink-0">
                  7
                </span>
                <div>
                  <h4 className="text-sm font-bold text-white group-hover:text-amber-300 transition-colors">
                    How far along are you?
                  </h4>
                  <span className="text-[10px] text-gray-400 font-mono">
                    Production deployment, pilot users, infrastructure grants &amp; performance metrics
                  </span>
                </div>
              </button>
              <div className="flex items-center gap-2 shrink-0 ml-3">
                <button
                  type="button"
                  onClick={() => copyToClipboard(SOVEREIGN_YC_APPLICATION_DATA.howFarAlong, "Question 7", "q7")}
                  className="p-1.5 text-gray-400 hover:text-amber-400 transition cursor-pointer rounded-lg hover:bg-[#20272B]"
                  title="Copy this answer"
                >
                  {copiedKey === 'q7' ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                </button>
                <button
                  type="button"
                  onClick={() => setExpandedSection(prev => prev === 'q7' ? null : 'q7')}
                  className="p-1.5 text-gray-400 hover:text-white transition cursor-pointer"
                  aria-label="Toggle answer accordion"
                >
                  {expandedSection === 'q7' ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                </button>
              </div>
            </div>

            {expandedSection === 'q7' && (
              <div className="p-5 border-t border-[#1F272B] bg-[#0E1214] text-xs text-gray-300 leading-relaxed whitespace-pre-wrap space-y-3 font-sans">
                {SOVEREIGN_YC_APPLICATION_DATA.howFarAlong}
              </div>
            )}
          </div>

          {/* QUESTION 8: TECH STACK, AI MODELS & CODING TOOLS */}
          <div className="border border-[#263137] bg-[#111518] rounded-2xl overflow-hidden">
            <div className="w-full p-4 flex items-center justify-between text-left hover:bg-[#161C1F] transition">
              <button
                type="button"
                onClick={() => setExpandedSection(prev => prev === 'q8' ? null : 'q8')}
                className="flex-1 flex items-center gap-3 text-left cursor-pointer group"
              >
                <span className="w-6 h-6 rounded-lg bg-amber-400/10 text-amber-400 font-mono font-bold text-xs flex items-center justify-center shrink-0">
                  8
                </span>
                <div>
                  <h4 className="text-sm font-bold text-white group-hover:text-amber-300 transition-colors">
                    What tech stack are you using, or planning to use?
                  </h4>
                  <span className="text-[10px] text-gray-400 font-mono">
                    Air-gapped client vault, React 19, Gemini 2.5/3 Pro/Flash, Live API &amp; AI coding tools
                  </span>
                </div>
              </button>
              <div className="flex items-center gap-2 shrink-0 ml-3">
                <button
                  type="button"
                  onClick={() => copyToClipboard(SOVEREIGN_YC_APPLICATION_DATA.techStack, "Question 8", "q8")}
                  className="p-1.5 text-gray-400 hover:text-amber-400 transition cursor-pointer rounded-lg hover:bg-[#20272B]"
                  title="Copy this answer"
                >
                  {copiedKey === 'q8' ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                </button>
                <button
                  type="button"
                  onClick={() => setExpandedSection(prev => prev === 'q8' ? null : 'q8')}
                  className="p-1.5 text-gray-400 hover:text-white transition cursor-pointer"
                  aria-label="Toggle answer accordion"
                >
                  {expandedSection === 'q8' ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                </button>
              </div>
            </div>

            {expandedSection === 'q8' && (
              <div className="p-5 border-t border-[#1F272B] bg-[#0E1214] text-xs text-gray-300 leading-relaxed whitespace-pre-wrap space-y-3 font-sans">
                {SOVEREIGN_YC_APPLICATION_DATA.techStack}
              </div>
            )}
          </div>

          {/* QUESTION 9: COMPETITORS & UNFAIR ADVANTAGE */}
          <div className="border border-[#263137] bg-[#111518] rounded-2xl overflow-hidden">
            <div className="w-full p-4 flex items-center justify-between text-left hover:bg-[#161C1F] transition">
              <button
                type="button"
                onClick={() => setExpandedSection(prev => prev === 'q9' ? null : 'q9')}
                className="flex-1 flex items-center gap-3 text-left cursor-pointer group"
              >
                <span className="w-6 h-6 rounded-lg bg-amber-400/10 text-amber-400 font-mono font-bold text-xs flex items-center justify-center shrink-0">
                  9
                </span>
                <div>
                  <h4 className="text-sm font-bold text-white group-hover:text-amber-300 transition-colors">
                    Who are your competitors? What do you understand that they don't?
                  </h4>
                  <span className="text-[10px] text-gray-400 font-mono">
                    QuickBooks, Mercury, HoneyBook vs. pre-execution margin defense &amp; anti-sycophancy
                  </span>
                </div>
              </button>
              <div className="flex items-center gap-2 shrink-0 ml-3">
                <button
                  type="button"
                  onClick={() => copyToClipboard(SOVEREIGN_YC_APPLICATION_DATA.competitorsAndUnfairAdvantage, "Question 9", "q9")}
                  className="p-1.5 text-gray-400 hover:text-amber-400 transition cursor-pointer rounded-lg hover:bg-[#20272B]"
                  title="Copy this answer"
                >
                  {copiedKey === 'q9' ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                </button>
                <button
                  type="button"
                  onClick={() => setExpandedSection(prev => prev === 'q9' ? null : 'q9')}
                  className="p-1.5 text-gray-400 hover:text-white transition cursor-pointer"
                  aria-label="Toggle answer accordion"
                >
                  {expandedSection === 'q9' ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                </button>
              </div>
            </div>

            {expandedSection === 'q9' && (
              <div className="p-5 border-t border-[#1F272B] bg-[#0E1214] text-xs text-gray-300 leading-relaxed whitespace-pre-wrap space-y-3 font-sans">
                {SOVEREIGN_YC_APPLICATION_DATA.competitorsAndUnfairAdvantage}
              </div>
            )}
          </div>

          {/* QUESTION 10: WHAT CONVINCED YOU TO APPLY TO Y COMBINATOR? */}
          <div className="border border-[#263137] bg-[#111518] rounded-2xl overflow-hidden">
            <div className="w-full p-4 flex items-center justify-between text-left hover:bg-[#161C1F] transition">
              <button
                type="button"
                onClick={() => setExpandedSection(prev => prev === 'q10' ? null : 'q10')}
                className="flex-1 flex items-center gap-3 text-left cursor-pointer group"
              >
                <span className="w-6 h-6 rounded-lg bg-amber-400/10 text-amber-400 font-mono font-bold text-xs flex items-center justify-center shrink-0">
                  10
                </span>
                <div>
                  <h4 className="text-sm font-bold text-white group-hover:text-amber-300 transition-colors">
                    What convinced you to apply to Y Combinator?
                  </h4>
                  <span className="text-[10px] text-gray-400 font-mono">
                    Garry Tan / Dalton Caldwell 1-person unicorn thesis, AltaLab encouragement &amp; global cohort scale
                  </span>
                </div>
              </button>
              <div className="flex items-center gap-2 shrink-0 ml-3">
                <button
                  type="button"
                  onClick={() => copyToClipboard(SOVEREIGN_YC_APPLICATION_DATA.whatConvincedYouToApply, "Question 10", "q10")}
                  className="p-1.5 text-gray-400 hover:text-amber-400 transition cursor-pointer rounded-lg hover:bg-[#20272B]"
                  title="Copy this answer"
                >
                  {copiedKey === 'q10' ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                </button>
                <button
                  type="button"
                  onClick={() => setExpandedSection(prev => prev === 'q10' ? null : 'q10')}
                  className="p-1.5 text-gray-400 hover:text-white transition cursor-pointer"
                  aria-label="Toggle answer accordion"
                >
                  {expandedSection === 'q10' ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                </button>
              </div>
            </div>

            {expandedSection === 'q10' && (
              <div className="p-5 border-t border-[#1F272B] bg-[#0E1214] text-xs text-gray-300 leading-relaxed whitespace-pre-wrap space-y-3 font-sans">
                {SOVEREIGN_YC_APPLICATION_DATA.whatConvincedYouToApply}
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 3: STRATEGIC THESIS & PARTNER DRILL */}
      {activeTab === 'strategy' && (
        <div className="space-y-6">
          <div className="p-6 bg-[#111518] border border-[#263137] rounded-2xl space-y-4">
            <h3 className="text-base font-bold text-white flex items-center gap-2">
              <Sparkles size={18} className="text-amber-400" />
              <span>Why Y Combinator Partners Will Love This in 2026/2027</span>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div className="p-4 bg-[#161B1E] border border-[#273238] rounded-xl space-y-2">
                <div className="font-bold text-amber-300 text-sm">1. The "1-Person Unicorn" Thesis is Live</div>
                <p className="text-gray-300 leading-relaxed">
                  Every partner in Silicon Valley (Garry Tan, Dalton Caldwell, Sam Altman) believes that the next massive technology companies will be run by ultra-lean teams of 1 to 5 people using autonomous agents. But nobody has built the <strong>System of Record</strong> for those operators. Azura Sovereign is that System of Record.
                </p>
              </div>

              <div className="p-4 bg-[#161B1E] border border-[#273238] rounded-xl space-y-2">
                <div className="font-bold text-amber-300 text-sm">2. You Aren't Theorizing — You Dogfooded It</div>
                <p className="text-gray-300 leading-relaxed">
                  Most founders pitch an "agent" after writing 5 prompts. You built three separate production engines because you literally had to run your company with them. That gives you an authentic, visceral narrative that cannot be faked in a 10-minute partner interview.
                </p>
              </div>

              <div className="p-4 bg-[#161B1E] border border-[#273238] rounded-xl space-y-2">
                <div className="font-bold text-amber-300 text-sm">3. The Accelerator B2B Wedge</div>
                <p className="text-gray-300 leading-relaxed">
                  Instead of paying $100 per lead on Facebook ads, you sell to the exact entities that gather hundreds of these founders: accelerators (YC, Techstars, Antler, AltaLab). By providing cohort health dashboards, accelerators become your enterprise distribution channel.
                </p>
              </div>

              <div className="p-4 bg-[#161B1E] border border-[#273238] rounded-xl space-y-2">
                <div className="font-bold text-amber-300 text-sm">4. Defensible Moat Against Generic LLMs</div>
                <p className="text-gray-300 leading-relaxed">
                  ChatGPT cannot reconcile raw CSV/PDF bank statements in 0.05s client-side without storing passwords. ChatGPT cannot legally guarantee safe-to-spend tax reserves. By coupling deep local ledger logic with non-sycophantic evaluation, you have a true technical and data moat.
                </p>
              </div>
            </div>
          </div>

          {/* MOCK ADVERSARIAL PARTNER QUESTIONS */}
          <div className="p-6 bg-[#111518] border border-[#263137] rounded-2xl space-y-4">
            <h3 className="text-base font-bold text-white flex items-center gap-2">
              <Target size={18} className="text-rose-400" />
              <span>Adversarial Partner Counter-Drills (How to Defend the Triad in 10 Seconds)</span>
            </h3>

            <div className="space-y-3 text-xs">
              <div className="p-4 bg-[#161B1E] border border-[#273238] rounded-xl space-y-2">
                <div className="font-bold text-rose-300 font-mono">
                  Partner: "Are you boiling the ocean? Why aren't you focused on just one tool?"
                </div>
                <div className="text-gray-300 pl-3 border-l-2 border-emerald-400 font-sans leading-relaxed">
                  <strong className="text-emerald-300">Your Response:</strong> "We aren't building three random products; we built the <em>exact minimum viable desk</em> for a solo operator. An accounting ledger alone is useless if the founder is building something nobody wants. And a GTM diagnostic is useless if the founder doesn't have the voice ops to close enterprise pilots. We engineered these in three disciplined phases to dogfood our own zero-dilution survival, and now we package that exact survival kit for every cohort builder."
                </div>
              </div>

              <div className="p-4 bg-[#161B1E] border border-[#273238] rounded-xl space-y-2">
                <div className="font-bold text-rose-300 font-mono">
                  Partner: "Why won't Stripe or QuickBooks just copy this?"
                </div>
                <div className="text-gray-300 pl-3 border-l-2 border-emerald-400 font-sans leading-relaxed">
                  <strong className="text-emerald-300">Your Response:</strong> "QuickBooks is built for accountants weeks after money moves; they rely on Plaid credential scraping which founders increasingly reject. Stripe is an acquirer that wants you to spend. Neither of them has an air-gapped local-first tax vault, nor do they provide non-sycophantic ICP diagnostics or AI voice operations. We occupy the founder's active decision bridge, not the post-mortem audit ledger."
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
